/**
 * Standalone time picker using shadcn Select components.
 *
 * Accepts and returns "HH:MM" 24h format strings to match existing state interfaces
 * (e.g. PushToScreensDialog reducer). That contract does NOT change with the
 * display mode: the same selection emits the same string whether the picker is
 * showing 12-hour with an AM/PM toggle or 24-hour without one (#2067).
 *
 * Extracted from the time portion of DateTimePicker.tsx for reuse without date.
 */
import { Clock } from 'lucide-react';
import {
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@scio3/ui';
import { is24HourPattern, useTimeFormat } from '@/hooks/useTimeFormat';

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0, 1, 2, ... 59

function to12Hour(h24: number): { h12: number; period: 'AM' | 'PM' } {
  return { h12: h24 % 12 || 12, period: h24 >= 12 ? 'PM' : 'AM' };
}

function to24Hour(h12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

export interface TimePickerProps {
  /** Time value in "HH:MM" 24h format */
  value: string;
  /** Callback with "HH:MM" 24h format string */
  onChange: (value: string) => void;
  /** Accessible label for the picker */
  'aria-label'?: string;
  /** dayjs time pattern — uppercase H switches the picker to 24-hour mode.
   *  Omit it to follow the profile Time Format (#2067); pass one to pin the
   *  picker regardless of the preference, which is what tests and Storybook want. */
  timeFormat?: string;
}

export function TimePicker({
  value,
  onChange,
  'aria-label': ariaLabel,
  timeFormat,
}: TimePickerProps) {
  const [hStr, mStr] = value.split(':');
  const h24 = parseInt(hStr, 10) || 0;
  const minute = parseInt(mStr, 10) || 0;
  const { h12, period } = to12Hour(h24);

  // Same rule as DateTimePicker: an explicit prop pins the mode, otherwise the
  // profile preference decides, and nothing stored stays 12-hour as before.
  const profileTimeFormat = useTimeFormat();
  const effectiveTimeFormat = timeFormat ?? profileTimeFormat.pattern;
  const is24Hour = effectiveTimeFormat ? is24HourPattern(effectiveTimeFormat) : false;

  const emit = (hours24: number, mins: number) => {
    const hh = String(hours24).padStart(2, '0');
    const mm = String(mins).padStart(2, '0');
    onChange(`${hh}:${mm}`);
  };

  return (
    <div className="flex items-center gap-1.5" aria-label={ariaLabel} role="group">
      <Clock className="h-4 w-4 shrink-0 text-foreground" />
      <Select
        value={String(is24Hour ? h24 : h12)}
        onValueChange={(v) => emit(is24Hour ? Number(v) : to24Hour(Number(v), period), minute)}
      >
        <SelectTrigger className="h-8 w-[60px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <ScrollArea className="h-48">
            {(is24Hour ? HOURS_24 : HOURS_12).map((h) => (
              <SelectItem key={h} value={String(h)}>
                {is24Hour ? String(h).padStart(2, '0') : h}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      <span className="text-sm text-muted-foreground">:</span>

      <Select value={String(minute)} onValueChange={(v) => emit(h24, Number(v))}>
        <SelectTrigger className="h-8 w-[60px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <ScrollArea className="h-48">
            {MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)}>
                {String(m).padStart(2, '0')}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      {!is24Hour && (
        <Select value={period} onValueChange={(v) => emit(to24Hour(h12, v as 'AM' | 'PM'), minute)}>
          <SelectTrigger className="h-8 w-[65px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
